<template>
  <div class="aliceblue-auth">
    <!-- Not connected -->
    <div v-if="!isAuthenticated" class="auth-panel">
      <div class="auth-header">
        <div class="auth-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="12" fill="#2563EB"/>
            <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-weight="700" font-size="20" fill="#fff">A</text>
          </svg>
        </div>
        <div class="auth-title">
          <h3>Connect AliceBlue</h3>
          <p class="auth-subtitle">Access NSE, BSE & F&O data with real-time feeds</p>
        </div>
      </div>

      <div class="auth-features">
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Real-time market data via WebSocket</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>NSE, BSE & F&O segments</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Historical candle data</span>
        </div>
      </div>

      <!-- Referral & Setup Guide -->
      <div class="broker-setup-guide">
        <div class="referral-section">
          <div class="referral-header">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
            </svg>
            <span>Don't have an AliceBlue account?</span>
          </div>
          <a
            href="https://ekyc.aliceblueonline.com/?source=SKAR723"
            target="_blank"
            rel="noopener noreferrer"
            class="referral-link"
          >
            Open AliceBlue Account (Free)
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
            </svg>
          </a>
        </div>

        <button class="setup-guide-toggle" @click="showSetupGuide = !showSetupGuide">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          {{ showSetupGuide ? 'Hide' : 'Show' }} API Setup Guide
        </button>

        <div v-if="showSetupGuide" class="setup-guide-content">
          <h4>How to get your AliceBlue API Key (Free)</h4>
          <ol class="setup-steps">
            <li>
              <strong>Create an AliceBlue Account</strong> — 
              <a href="https://ekyc.aliceblueonline.com/?source=SKAR723" target="_blank" rel="noopener noreferrer">Sign up here</a> 
              if you don't have one. Complete eKYC verification.
            </li>
            <li>
              <strong>Go to AliceBlue Developer Console</strong> — 
              Visit <a href="https://a3.aliceblueonline.com/devportal" target="_blank" rel="noopener noreferrer">AliceBlue Developer Portal</a> and log in.
            </li>
            <li>
              <strong>Create a New App</strong> — Click "Create App" and fill in:
              <ul class="setup-sub-steps">
                <li><strong>App Name:</strong> Any name (e.g., "TradingCandle")</li>
                <li><strong>Redirect URL:</strong> <code>https://tradingcandle.com/api/aliceblue/callback</code></li>
                <li><strong>App Type:</strong> Select "Trading" or "Data"</li>
              </ul>
            </li>
            <li>
              <strong>Get Your Credentials</strong> — After app creation, copy your:
              <ul class="setup-sub-steps">
                <li><strong>App Code</strong> (Client ID)</li>
                <li><strong>API Secret</strong></li>
              </ul>
            </li>
            <li>
              <strong>Connect Below</strong> — Click "Connect with AliceBlue" and enter your App Code and Secret when prompted.
            </li>
          </ol>
          <p class="setup-note">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
            AliceBlue API access is free with your trading account. No minimum balance required.
          </p>
        </div>
      </div>

      <!-- OAuth Authentication -->
      <div class="auth-button-group">
        <button
          @click="connectWithOAuth"
          :disabled="isLoading"
          class="auth-button"
        >
          <span v-if="isLoading" class="btn-loading">
            <svg class="spinner" width="20" height="20" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
              </circle>
            </svg>
            Connecting to AliceBlue...
          </span>
          <span v-else>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 12l2 2 4-4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            Connect with AliceBlue
          </span>
        </button>
        
        <button 
          v-if="requiresUserCredentials && hasStoredCredentials" 
          @click="showChangeCredentialsModal" 
          class="change-credentials-button"
          :disabled="isLoading"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L14.293 8.5H.5a.5.5 0 0 1 0-1h13.793L2.146 2.146a.5.5 0 0 1 0-.708l-3-3z"/>
          </svg>
          Change Credentials
        </button>
      </div>

      <div v-if="error" class="error-message">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 16A8 8 0 108 0a8 8 0 000 16zM5.354 4.646a.5.5 0 10-.708.708L7.293 8l-2.647 2.646a.5.5 0 00.708.708L8 8.707l2.646 2.647a.5.5 0 00.708-.708L8.707 8l2.647-2.646a.5.5 0 00-.708-.708L8 7.293 5.354 4.646z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <!-- Help section -->
      <div class="help-section">
        <button class="help-toggle" @click="showHelp = !showHelp">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"/>
          </svg>
          How OAuth Authentication Works
        </button>
        <div v-if="showHelp" class="help-content">
          <ol class="help-steps">
            <li>Click <strong>Connect with AliceBlue</strong> to open the login popup</li>
            <li>Log in with your AliceBlue credentials in the popup window</li>
            <li>Authorize TradingCandle to access your account data</li>
            <li>The popup will close automatically and you'll be connected</li>
          </ol>
          <p class="help-note">
            This secure OAuth flow ensures your credentials never leave AliceBlue's servers.
          </p>
        </div>
      </div>
    </div>

    <!-- Connected state -->
    <div v-else class="auth-connected">
      <div class="connected-header">
        <div class="connected-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="12" fill="#22c55e"/>
            <path d="M16 24l6 6 10-10" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="connected-info">
          <h3>AliceBlue Connected</h3>
          <p class="connected-subtitle">Your account is linked and receiving data</p>
        </div>
      </div>

      <div class="connected-details">
        <div class="detail-row">
          <span class="detail-label">User ID</span>
          <span class="detail-value">{{ userId || 'Connected' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Provider</span>
          <span class="detail-value">AliceBlue</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value status-active">
            <span class="status-dot"></span>
            Active
          </span>
        </div>
      </div>

      <div class="auth-actions">
        <button
          @click="connectWithOAuth"
          class="auth-button reconnect-action"
          :disabled="isLoading"
        >
          Reconnect
        </button>
        <button
          v-if="requiresUserCredentials && hasStoredCredentials"
          @click="showChangeCredentialsModal"
          class="change-credentials-button"
          :disabled="isLoading"
        >
          Change Credentials
        </button>
        <button @click="disconnect" class="disconnect-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Disconnect AliceBlue
        </button>
      </div>
    </div>

    <!-- Broker Credentials Modal -->
    <BrokerCredentialsModal
      :show="showCredentialsModal"
      :broker-type="'aliceblue'"
      :has-stored-credentials="hasStoredCredentials"
      :stored-api-key="storedApiKey"
      :start-in-edit="credentialModalStartsInEdit"
      @close="closeCredentialsModal"
      @save="saveCredentials"
      @use-stored="useStoredCredentials"
    />
  </div>
</template>

<script>
import AliceBlueService from '../services/aliceblue_service.js';
import DataProviderManager from '../services/data_provider_manager.js';
import BrokerCredentialsModal from './BrokerCredentialsModal.vue';
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || '';

export default {
  name: 'AliceBlueAuth',
  components: {
    BrokerCredentialsModal
  },
  props: {
    credentialSource: {
      type: String,
      default: null,
      validator: value => value === null || ['env', 'user'].includes(value)
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      isAuthenticated: false,
      userId: null,
      showHelp: false,
      showSetupGuide: false,
      showCredentialsModal: false,
      credentialModalStartsInEdit: false,
      credentialModalMode: 'manage',
      hasStoredCredentials: false,
      storedApiKey: '',
      requiresUserCredentials: this.credentialSource ? this.credentialSource === 'user' : true
    };
  },

  async mounted() {
    this.checkAuthenticationStatus();
    await this.checkStoredCredentials();
  },
  watch: {
    credentialSource(newSource) {
      if (newSource) {
        this.requiresUserCredentials = newSource === 'user';
      }
    }
  },

  methods: {
    checkAuthenticationStatus() {
      this.isAuthenticated = AliceBlueService.isAuthenticated();
      if (this.isAuthenticated) {
        this.userId = AliceBlueService.userId || localStorage.getItem('aliceblue_userId');
      }
    },

    /**
     * Open AliceBlue login. In Tauri, uses in-app WebView; in web, uses popup.
     * After user logs in, AliceBlue redirects to our server's /api/aliceblue/callback
     * which stores the auth code. We then call /api/aliceblue/complete to exchange
     * the code for a session token stored in Clerk metadata.
     */
    async connectWithOAuth(skipCredentialRefresh = false) {
      this.isLoading = true;
      this.error = null;

      try {
        const credentialSource = await this.fetchCredentialSource();
        if (credentialSource) {
          this.requiresUserCredentials = credentialSource === 'user';
        }

        let credentialDetails = null;
        if (!skipCredentialRefresh) {
          credentialDetails = await this.checkStoredCredentials({ preserveOnError: true });
        }

        // Check if user has stored credentials first
        const hasCredentials = credentialDetails ? credentialDetails.hasCredentials : this.hasStoredCredentials;
        if (this.requiresUserCredentials && !hasCredentials) {
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.isLoading = false;
          return;
        }

        const authData = await AliceBlueService.getAuthUrl();

        // Check if running in Tauri
        if (window.__TAURI__) {
          // Tauri environment - open OAuth in an in-app WebView window
          await this.openTauriOAuthWindow(authData.authUrl, authData.state);
          return;
        }

        // Web environment - open in popup
        const popup = window.open(authData.authUrl, 'aliceblue_login', 'width=600,height=700');

        // Poll for the popup to close (user finished login)
        const pollInterval = setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(pollInterval);

              // Small delay to ensure the callback has been processed
              await new Promise(resolve => setTimeout(resolve, 1500));

              // Popup closed — complete the login via the server.
              // Server exchanges the auth code for a token and stores it in Clerk.
              try {
                const result = await AliceBlueService.completeLogin(authData.state);
                this.isAuthenticated = true;
                this.userId = result.userId;
                DataProviderManager.switchProvider('aliceblue');
                this.$emit('authenticated', result);
              } catch (completeErr) {
                this.error = completeErr.response?.data?.error || completeErr.message;
              }
              this.isLoading = false;
            }
          } catch (e) {
            // Cross-origin access error is expected while popup is on AliceBlue domain
          }
        }, 1000);

        // Safety timeout — stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (this.isLoading) {
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        if (error.response?.data?.credentialSource === 'user') {
          this.requiresUserCredentials = true;
          this.hasStoredCredentials = false;
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.error = null;
          this.isLoading = false;
          return;
        }

        this.error = 'Failed to connect: ' + (error.response?.data?.error || error.message);
        this.isLoading = false;
      }
    },

    /**
     * Open OAuth in a Tauri WebviewWindow (in-app browser).
     */
    async openTauriOAuthWindow(authUrl, state) {
      try {
        const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');

        const oauthWindow = new WebviewWindow('aliceblue-oauth', {
          url: authUrl,
          title: 'AliceBlue Login',
          width: 600,
          height: 700,
          center: true,
          resizable: true,
          decorations: true
        });

        let isCompleted = false;
        let checkInterval = null;

        oauthWindow.once('tauri://destroyed', async () => {
          if (!isCompleted) {
            clearInterval(checkInterval);
            await this.completeOAuthFlow(state);
          }
        });

        checkInterval = setInterval(async () => {
          try {
            const status = await AliceBlueService.checkAuthStatus();
            if (status.success) {
              isCompleted = true;
              clearInterval(checkInterval);
              oauthWindow.close();
              await this.completeOAuthFlow(state);
            }
          } catch (e) {
            // Ignore errors during polling
          }
        }, 2000);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!isCompleted && this.isLoading) {
            oauthWindow.close();
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        console.error('Failed to open Tauri OAuth window:', error);
        window.open(authUrl, '_blank');
        this.pollForTauriAuthCompletion(state);
      }
    },

    /**
     * Complete the OAuth flow after user authentication.
     */
    async completeOAuthFlow(state) {
      try {
        const result = await AliceBlueService.completeLogin(state);
        this.isAuthenticated = true;
        this.userId = result.userId;
        DataProviderManager.switchProvider('aliceblue');
        this.$emit('authenticated', result);
      } catch (completeErr) {
        this.error = completeErr.response?.data?.error || completeErr.message;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fallback polling method for when using system browser in Tauri.
     */
    async pollForTauriAuthCompletion(state) {
      const maxAttempts = 150;
      let attempts = 0;

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const status = await AliceBlueService.checkAuthStatus();

          if (status.success) {
            clearInterval(pollInterval);
            this.isAuthenticated = true;
            this.userId = status.userId;
            DataProviderManager.switchProvider('aliceblue');
            this.$emit('authenticated', status);
            this.isLoading = false;
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        } catch (error) {
          // Continue polling on error
        }
      }, 2000);
    },

    async disconnect() {
      try {
        await AliceBlueService.disconnectFromServer();
      } catch (e) {
        // Ignore disconnect errors
      }
      this.isAuthenticated = false;
      this.userId = null;

      // Switch back to default provider
      DataProviderManager.switchProvider('binance');

      this.$emit('disconnected');
    },

    async checkStoredCredentials(options = {}) {
      try {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/broker-credentials/aliceblue/details', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        this.hasStoredCredentials = response.data.hasCredentials;
        this.storedApiKey = response.data.apiKey || '';
        this.requiresUserCredentials = response.data.requiresUserCredentials === true;
        return response.data;
      } catch (error) {
        console.warn('[AliceBlue] Could not check stored credentials:', error.message);
        if (!options.preserveOnError) {
          this.hasStoredCredentials = false;
          this.storedApiKey = '';
          this.requiresUserCredentials = this.credentialSource ? this.credentialSource === 'user' : true;
        }
        return null;
      }
    },

    async fetchCredentialSource() {
      try {
        const response = await axios.get((API_BASE || window.location.origin) + '/api/integrations/status');
        const setting = response.data?.integrations?.aliceblue;
        return setting && setting.credentialSource ? setting.credentialSource : null;
      } catch (error) {
        console.warn('[AliceBlue] Could not fetch credential source:', error.message);
        return null;
      }
    },

    async saveCredentials({ credentials }, controls) {
      try {
        const token = await this._getClerkToken();
        await axios.post(API_BASE + '/api/broker-credentials/save', {
          brokerType: 'aliceblue',
          credentials
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        this.hasStoredCredentials = true;
        this.storedApiKey = credentials.apiKey;
        this.error = null;
        this.showCredentialsModal = false;
        this.credentialModalStartsInEdit = false;

        if (controls && controls.resolve) controls.resolve();

        this.$emit('credentials-saved', 'aliceblue');

        if (this.credentialModalMode === 'connect') {
          this.$nextTick(() => {
            this.connectWithOAuth(true);
          });
        }
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to save credentials';
        if (controls && controls.reject) {
          controls.reject(new Error(message));
          return;
        }
        throw new Error(message);
      }
    },

    useStoredCredentials() {
      this.connectWithOAuth();
    },

    showChangeCredentialsModal() {
      this.credentialModalMode = 'manage';
      this.credentialModalStartsInEdit = this.hasStoredCredentials;
      this.showCredentialsModal = true;
    },

    closeCredentialsModal() {
      this.showCredentialsModal = false;
      this.credentialModalStartsInEdit = false;
      this.credentialModalMode = 'manage';
    },

    async _getClerkToken() {
      try {
        if (window.__vue_clerk && window.__vue_clerk.getToken) {
          return await window.__vue_clerk.getToken();
        }
        if (window.Clerk && window.Clerk.session) {
          return await window.Clerk.session.getToken();
        }
        if (window.Clerk && window.Clerk.getToken) {
          return await window.Clerk.getToken();
        }
      } catch (e) {
        console.warn('[AliceBlue] Could not get Clerk token:', e.message);
      }
      return null;
    }
  }
};
</script>

<style scoped>
/* Theme-aware via global CSS variables defined in App.vue (data-theme="dark"). */

.aliceblue-auth {
  max-width: 480px;
  margin: 0 auto;
  color: var(--tc-text-primary);
}

.auth-panel {
  padding: 0;
}

.auth-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.auth-title h3 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.auth-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
}

.auth-features {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--tc-bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--tc-border-primary);
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--tc-text-primary);
}

.feature-item svg {
  color: var(--tc-accent);
  flex-shrink: 0;
}

.auth-form {
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--tc-input-border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--tc-input-text);
  background: var(--tc-input-bg);
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--tc-accent);
  box-shadow: 0 0 0 3px var(--tc-accent-bg);
}

.form-input::placeholder {
  color: var(--tc-input-placeholder);
}

.auth-button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.auth-button {
  flex: 1;
  padding: 16px 24px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.change-credentials-button {
  padding: 16px 20px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-primary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.change-credentials-button:hover {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
}

.auth-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
}

.auth-button:active:not(:disabled) {
  transform: scale(0.98);
}

.auth-button:disabled {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

.auth-button span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  background: var(--tc-red-bg);
  color: var(--tc-red);
  border: 1px solid var(--tc-red);
  border-radius: 8px;
  font-size: 13px;
}

.error-message svg {
  flex-shrink: 0;
}

/* Help section */
.help-section {
  margin-top: 20px;
  border-top: 1px solid var(--tc-border-primary);
  padding-top: 16px;
}

.help-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: var(--tc-accent);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
}

.help-toggle:hover {
  color: var(--tc-accent-hover);
}

.help-content {
  margin-top: 12px;
  padding: 16px;
  background: var(--tc-bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--tc-border-primary);
}

.help-steps {
  margin: 0 0 12px;
  padding-left: 20px;
  font-size: 13px;
  color: var(--tc-text-primary);
  line-height: 1.8;
}

.help-steps a {
  color: var(--tc-accent);
  text-decoration: none;
}

.help-steps a:hover {
  text-decoration: underline;
}

.help-note {
  margin: 0;
  font-size: 12px;
  color: var(--tc-text-secondary);
  font-style: italic;
}

/* Connected state */
.auth-connected {
  padding: 0;
}

.connected-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.connected-info h3 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: var(--tc-green);
}

.connected-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
}

.connected-details {
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.detail-row:not(:last-child) {
  border-bottom: 1px solid var(--tc-border-primary);
}

.detail-label {
  font-size: 13px;
  color: var(--tc-text-secondary);
}

.detail-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--tc-text-primary);
}

.status-active {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--tc-green);
}

.status-active .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--tc-green);
}

.auth-actions {
  display: flex;
  gap: 12px;
}

.auth-actions .reconnect-action {
  flex: 0 1 auto;
}

.disconnect-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--tc-card-bg);
  color: var(--tc-red);
  border: 1px solid var(--tc-red);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.disconnect-button:hover {
  background: var(--tc-red-bg);
  border-color: var(--tc-red);
}

/* Broker Setup Guide & Referral Styles */
.broker-setup-guide {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
}

.referral-section {
  margin-bottom: 12px;
}

.referral-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--tc-text-secondary);
  margin-bottom: 8px;
}

.referral-header svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.referral-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  border: 1px solid var(--tc-accent);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.referral-link:hover {
  background: var(--tc-accent);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.setup-guide-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  color: var(--tc-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.setup-guide-toggle:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
  border-color: var(--tc-border-hover);
}

.setup-guide-toggle svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.setup-guide-content {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tc-border-primary);
}

.setup-guide-content h4 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.setup-steps {
  margin: 0 0 16px;
  padding-left: 20px;
  list-style: decimal;
}

.setup-steps li {
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--tc-text-primary);
}

.setup-steps li a {
  color: var(--tc-accent);
  text-decoration: none;
  font-weight: 500;
}

.setup-steps li a:hover {
  text-decoration: underline;
}

.setup-sub-steps {
  margin: 8px 0 0;
  padding-left: 18px;
  list-style: disc;
}

.setup-sub-steps li {
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--tc-text-secondary);
}

.setup-sub-steps li code {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-primary);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 11px;
}

.setup-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  background: var(--tc-accent-bg);
  border-radius: 8px;
  font-size: 12px;
  color: var(--tc-accent);
  line-height: 1.5;
}

.setup-note svg {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>
